import * as React from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { createSalesperson } from "@/Calls/callDealer";

const SalespersonSchema = z.object({
    salesperson_firstname: z.string().min(2, "First name required"),
    salesperson_lastname: z.string().min(2, "Last name required"),
    salesperson_email: z.email("Invalid email"),
    salesperson_username: z.string().optional(),
    salesperson_password: z.string().min(6, "Password must be at least 6 chars"),
    salesperson_mobilephone_number: z.string().optional(),
    salesperson_job_title: z.string().optional(),
});

type SalespersonForm = z.infer<typeof SalespersonSchema>;

export default function SalespersonCreatePage() {
    const form = useForm<SalespersonForm>({
        resolver: zodResolver(SalespersonSchema),
        defaultValues: {
            salesperson_firstname: "",
            salesperson_lastname: "",
            salesperson_email: "",
            salesperson_username: "",
            salesperson_password: "",
            salesperson_mobilephone_number: "",
            salesperson_job_title: "",
        },
    });

    const [submitting, setSubmitting] = React.useState(false);

    async function onSubmit(values: SalespersonForm) {
        try {
            setSubmitting(true);
            // const res = await fetch(`${API_URL}/dealers/sales-person-create`, {
            //     method: "POST",
            //     credentials: "include",
            //     headers: { "Content-Type": "application/json" },
            //     body: JSON.stringify(values),
            // });

            await createSalesperson(values);

           

            toast("✅ Salesperson created", {
                description: `${values.salesperson_firstname} ${values.salesperson_lastname} has been added successfully.`,
                action: {
                    label: "View Staff",
                    onClick: () => window.location.assign("/dealer/sales-staff"),
                },
            });

            form.reset();
        } catch (err) {
            console.error(err);

            // Type-guard: make sure it's an Error before accessing .message
            const message =
                err instanceof Error
                    ? err.message
                    : "Unexpected error — please try again later.";

            toast("❌ Error creating salesperson", {
                description: message,
            });
        } finally {
            setSubmitting(false);
        }
    }


    return (
        <div className="container mx-auto max-w-2xl py-8">
            <Card>
                <CardHeader>
                    <CardTitle>Add New Sales Person</CardTitle>
                </CardHeader>
                <CardContent>
                    <Form {...form}>
                        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
                            <div className="grid grid-cols-2 gap-4">
                                <FormField
                                    control={form.control}
                                    name="salesperson_firstname"
                                    render={({ field }) => (
                                        <FormItem>
                                            <FormLabel>First Name</FormLabel>
                                            <FormControl><Input {...field} placeholder="John" /></FormControl>
                                            <FormMessage />
                                        </FormItem>
                                    )}
                                />
                                <FormField
                                    control={form.control}
                                    name="salesperson_lastname"
                                    render={({ field }) => (
                                        <FormItem>
                                            <FormLabel>Last Name</FormLabel>
                                            <FormControl><Input {...field} placeholder="Doe" /></FormControl>
                                            <FormMessage />
                                        </FormItem>
                                    )}
                                />
                            </div>

                            <FormField
                                control={form.control}
                                name="salesperson_email"
                                render={({ field }) => (
                                    <FormItem>
                                        <FormLabel>Email</FormLabel>
                                        <FormControl><Input {...field} type="email" placeholder="email@dealer.com" /></FormControl>
                                        <FormMessage />
                                    </FormItem>
                                )}
                            />

                            <div className="grid grid-cols-1 gap-4">
                               
                                <FormField
                                    control={form.control}
                                    name="salesperson_password"
                                    render={({ field }) => (
                                        <FormItem>
                                            <FormLabel>Password</FormLabel>
                                            <FormControl><Input {...field} type="password" placeholder="********" /></FormControl>
                                            <FormMessage />
                                        </FormItem>
                                    )}
                                />
                            </div>

                            <FormField
                                control={form.control}
                                name="salesperson_mobilephone_number"
                                render={({ field }) => (
                                    <FormItem>
                                        <FormLabel>Mobile Phone</FormLabel>
                                        <FormControl><Input {...field} placeholder="(555) 555-5555" /></FormControl>
                                        <FormMessage />
                                    </FormItem>
                                )}
                            />

                            <FormField
                                control={form.control}
                                name="salesperson_job_title"
                                render={({ field }) => (
                                    <FormItem>
                                        <FormLabel>Job Title</FormLabel>
                                        <FormControl><Input {...field} placeholder="Sales Associate" /></FormControl>
                                        <FormMessage />
                                    </FormItem>
                                )}
                            />

                            <Button type="submit" disabled={submitting} className="w-full">
                                {submitting ? "Creating..." : "Create Salesperson"}
                            </Button>
                        </form>
                    </Form>
                </CardContent>
            </Card>
        </div>
    );
}
